import { isEmpty } from 'lodash';
import React from 'react';
import { getOrgTermsContent } from '../../../../content/utils.js';
import DateRangeFilter from './DateRangeFilter.jsx';
import EmployeesFilter from './EmployeesFilter.jsx';
import GroupsFilter from './GroupsFilter.jsx';
import ImpactFilter from './ImpactFilter.jsx';
import InitiativeCategoriesFilter from './InitiativeCategoriesFilter.jsx';
import InitiativeTypesFilter from './InitiativeTypesFilter.jsx';
import OwnersFilter from './OwnersFilter.jsx';
import PortfolioFilter from './PortfolioFilter.jsx';
import ProjectsFilter from './ProjectsFilter.jsx';
import ScopeFilter from './ScopeFilter.jsx';
import StatusFilter from './StatusFilter.jsx';
import SurveyFilter from './SurveyFilter.jsx';
import StakeholdersFilter from './StakeholdersFilter.jsx';
import { isAuthorized } from '../../../AuthorizedAction.js';
import ArchivedFilter from './ArchivedFilter.jsx';
import ProjectImpactFilter from './ProjectImpactFilter.jsx';
import MilestonesFilter from './MilestonesFilter.jsx';
import ProjectIcon from '../../../icons/ProjectIcon.jsx';
import Filters from '../../../filters/index.jsx';
import { navigateToProjectReports } from '../../../links/index.jsx';
import { resetFilters } from '../utils/index.js';
import NotesIcon from '../../../icons/NotesIcon.jsx';
import { ParticipantsIcon } from '../../../icons/ParticipantsIcon.jsx';
import { StakeholderBlueIcon } from '../../../icons/StakeholderBlueIcon.jsx';
import StakeholderInfluencesFilter from './StakeholderInfluencesFilter.jsx';
import StakeholderImpactsFilter from './StakeholderImpactsFilter.jsx';
import StakeholderInterestsFilter from './StakeholderInterestsFilter.jsx';
import StakeholderFrequencyFilter from './StakeholderFrequencyFilter.jsx';
import StakeholderSupportsFilter from './StakeholderSupportsFilter.jsx';
import ResponseStatusFilter from '../../../change-insights/ChangeInsightsFilters/ResponseStatusFilter.jsx';
import ParentPortfolioFilter from './ParentPortfolioFilter.jsx';

function ReportFilters({
  query,
  resetDependencies,
  reportType,
  permissions,
  hasParentPortfolios,
}) {
  const filtersData = [
    {
      key: 'project',
      title: getOrgTermsContent('project'),
      icon: <ProjectIcon />,
      preselectedFilterKeys: ['archived'],
      filters: [
        {
          key: 'parent-portfolio',
          title: getOrgTermsContent('parentPortfolio'),
          resetQueryParams: { parentPortfolioIds: [] },
          hidden: !hasParentPortfolios,
          component: (
            <ParentPortfolioFilter
              selectedParentPortfolioIds={query.parentPortfolioIds}
            />
          ),
        },
        {
          key: 'portfolio',
          title: getOrgTermsContent('portfolio'),
          resetQueryParams: { portfolioIds: [] },
          component: (
            <PortfolioFilter selectedPortfolioIds={query.portfolioIds} />
          ),
        },
        {
          key: 'project',
          title: getOrgTermsContent('project'),
          resetQueryParams: { projectIds: [] },
          component: <ProjectsFilter selectedProjectIds={query.projectIds} />,
        },
        {
          key: 'owner',
          title: 'Owner',
          resetQueryParams: { owners: [] },
          component: <OwnersFilter selectedOwnerIds={query.ownerIds} />,
        },
        {
          key: 'project-date-range',
          title: `${getOrgTermsContent('project')} Date Range`,
          resetQueryParams: { projectStartDate: null, projectEndDate: null },
          component: (
            <DateRangeFilter
              type="project"
              label={getOrgTermsContent('project')}
              startDate={query.projectStartDate}
              endDate={query.projectEndDate}
              isCapitalize={false}
            />
          ),
        },
        {
          key: 'status',
          title: 'Status',
          resetQueryParams: { projectStatusIds: [] },
          component: (
            <StatusFilter selectedStatusesIds={query.projectStatusIds} />
          ),
        },
        {
          key: 'archived',
          title: 'Archived',
          resetQueryParams: { archived: false },
          component: <ArchivedFilter selectedArchived={query.archived} />,
        },

        {
          key: 'project-impact',
          title: getOrgTermsContent('projectImpact'),
          resetQueryParams: { projectImpact: [] },
          component: (
            <ProjectImpactFilter selectedProjectImpacts={query.projectImpact} />
          ),
        },
        {
          key: 'project-scope',
          title: getOrgTermsContent('projectScope'),
          resetQueryParams: { scope: [] },
          component: <ScopeFilter selectedScopes={query.scope} />,
        },
        {
          key: 'portfolio-milestone',
          title: `${getOrgTermsContent('portfolio')} Milestone`,
          disabled: reportType !== 'gantt',
          resetQueryParams: { portfolioMilestoneIds: [] },
          component: (
            <MilestonesFilter
              type="portfolio"
              selectedMilestoneIds={query.portfolioMilestoneIds}
            />
          ),
        },
        {
          key: 'project-milestone',
          title: `${getOrgTermsContent('project')} Milestone`,
          disabled: reportType !== 'gantt',
          resetQueryParams: { projectMilestoneIds: [] },
          component: (
            <MilestonesFilter
              type="project"
              selectedMilestoneIds={query.projectMilestoneIds}
            />
          ),
        },
      ],
    },
    {
      key: 'initiative',
      title: getOrgTermsContent('initiatives'),
      icon: <NotesIcon />,
      filters: [
        {
          key: 'initiative-date-range',
          title: `${getOrgTermsContent('initiative')} Date Range`,
          resetQueryParams: {
            initiativeStartDate: null,
            initiativeEndDate: null,
          },
          component: (
            <DateRangeFilter
              type="initiative"
              label={getOrgTermsContent('initiative')}
              startDate={query.initiativeStartDate}
              endDate={query.initiativeEndDate}
              isCapitalize={false}
            />
          ),
        },
        {
          key: 'impact',
          title: getOrgTermsContent('initiativeImpact'),
          resetQueryParams: { initiativeImpact: [] },
          component: <ImpactFilter selectedImpacts={query.initiativeImpact} />,
        },
        {
          key: 'type',
          title: getOrgTermsContent('initiativeType'),
          resetQueryParams: { types: [] },
          component: (
            <InitiativeTypesFilter selectedTypes={query.initiativeTypes} />
          ),
        },
        {
          key: 'category',
          title: 'Category',
          resetQueryParams: { categories: [] },
          component: (
            <InitiativeCategoriesFilter
              selectedCategories={query.initiativeCategoryIds}
            />
          ),
        },
        {
          key: 'survey',
          title: 'Feedback Form',
          resetQueryParams: { survey: null },
          component: <SurveyFilter selectedOption={query.survey} />,
        },
        {
          key: 'response-status',
          title: 'Response Status',
          resetQueryParams: { surveyStatuses: [] },
          component: (
            <ResponseStatusFilter
              navigateAction={navigateToProjectReports}
              selectedStatusesIds={query.surveyStatuses}
            />
          ),
        },
        {
          key: 'survey-date-range',
          title: 'Feedback Form Date Range',
          resetQueryParams: { surveyStartDate: null, surveyEndDate: null },
          component: (
            <DateRangeFilter
              type="survey"
              label="Feedback Form"
              isCapitalize={false}
              startDate={query.surveyStartDate}
              endDate={query.surveyEndDate}
            />
          ),
        },
      ],
    },
    {
      key: 'participants',
      title: 'Participants',
      icon: <ParticipantsIcon />,
      filters: [
        {
          key: 'employee',
          title: 'Employee',
          resetQueryParams: { employees: [] },
          component: (
            <EmployeesFilter
              disabled={
                !isEmpty(query.groupIds) ||
                !isEmpty(query.organizationStakeholderIds)
              }
              selectedEmployeeIds={query.employeeIds}
            />
          ),
        },
        {
          key: 'group',
          title: 'Group',
          resetQueryParams: { groups: [] },
          component: (
            <GroupsFilter
              disabled={
                !isEmpty(query.employeeIds) ||
                !isEmpty(query.organizationStakeholderIds)
              }
              selectedGroupIds={query.groupIds}
            />
          ),
        },
        {
          key: 'stakeholder-group-individual',
          title: 'Stakeholder Group / Individual',
          disabled: !isAuthorized(
            'read',
            'organizationStakeholder',
            permissions
          ),
          resetQueryParams: { organizationStakeholderIds: [] },
          component: (
            <StakeholdersFilter
              disabled={!isEmpty(query.employeeIds) || !isEmpty(query.groupIds)}
              selectedStakeholdersIds={query.organizationStakeholderIds}
            />
          ),
        },
      ],
    },
    {
      key: 'stakeholder',
      title: 'Stakeholder',
      icon: <StakeholderBlueIcon />,
      filters: [
        {
          key: 'stakeholder-influence',
          title: 'Influence',
          resetQueryParams: { stakeholderInfluences: [] },
          component: (
            <StakeholderInfluencesFilter
              navigateAction={navigateToProjectReports}
              selectedStakeholderInfluences={query.stakeholderInfluences}
            />
          ),
        },
        {
          key: 'stakeholder-interest',
          title: 'Interest',
          resetQueryParams: { stakeholderInterests: [] },
          component: (
            <StakeholderInterestsFilter
              navigateAction={navigateToProjectReports}
              selectedStakeholderInterests={query.stakeholderInterests}
            />
          ),
        },
        {
          key: 'stakeholder-impacts',
          title: 'Predicted Impact',
          resetQueryParams: { stakeholderImpacts: [] },
          component: (
            <StakeholderImpactsFilter
              navigateAction={navigateToProjectReports}
              selectedStakeholderImpacts={query.stakeholderImpacts}
            />
          ),
        },
        {
          key: 'stakeholder-engagement-frequencies',
          title: 'Engagement Frequency',
          resetQueryParams: { stakeholderEngagementFrequencies: [] },
          component: (
            <StakeholderFrequencyFilter
              navigateAction={navigateToProjectReports}
              selectedStakeholderFrequencies={
                query.stakeholderEngagementFrequencies
              }
            />
          ),
        },
        {
          key: 'stakeholder-supports',
          title: 'Support for Change',
          resetQueryParams: { stakeholderSupports: [] },
          component: (
            <StakeholderSupportsFilter
              navigateAction={navigateToProjectReports}
              selectedStakeholderSupports={query.stakeholderSupports}
            />
          ),
        },
      ],
    },
  ];

  return (
    <Filters
      filtersData={filtersData}
      query={query}
      resetAction={navigateToProjectReports}
      resetAllAction={resetFilters}
      resetDependencies={resetDependencies}
    />
  );
}

export default ReportFilters;
